import { useState, useEffect } from "react";
import {
  ShieldAlert, RefreshCw, Loader2, ShieldCheck,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  green:  { bg: "bg-emerald-500/20", text: "text-emerald-300", label: "Propre" },
  yellow: { bg: "bg-amber-500/20",   text: "text-amber-300",   label: "À surveiller" },
  orange: { bg: "bg-orange-500/20",  text: "text-orange-300",  label: "Dégradé" },
  red:    { bg: "bg-red-500/20",     text: "text-red-300",     label: "Critique" },
};

const SEV = {
  critical: "bg-red-500/20 text-red-300",
  high:     "bg-orange-500/20 text-orange-300",
  warning:  "bg-amber-500/20 text-amber-300",
  dette:    "bg-blue-500/20 text-blue-300",
};

// Libellés courts des règles (mappées aux failles fermées)
const RULE_LABEL = {
  "R-TENANT-HARDCODE": "Tenant en dur / fail-to-tenant [I1]",
  "R-HEADER-AUTHORITY": "Header X-User-* comme autorité [I2]",
  "R-FILTER-FAILSOFT": "add_tenant_filter fail-soft [I4]",
  "R-SECRET-LOG": "Secret journalisé [R4a]",
  "R-STANDING-SESSION": "Session super-admin standing [R4b]",
};

export default function GouvRocket() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/rocket/latest`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setReport(d.report);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const runScan = async () => {
    setRunning(true);
    try {
      await fetch(`${API_URL}/api/rocket/scan`, { method: "POST", credentials: "include" });
      setTimeout(() => { loadData(); setRunning(false); }, 3000);
    } catch (e) { console.error(e); setRunning(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const colorCfg = COLORS[report?.health_color || report?.summary?.color || "green"];
  const summary = report?.summary || {};
  const bySev = summary.by_severity || {};
  const byRule = summary.by_rule || {};
  const findings = report?.findings || [];
  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div className="space-y-6" data-testid="rocket-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">
            🛡️ Rocket — Gardien Sécurité
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            {report
              ? `Dernier scan : ${fmtDate(report.scanned_at)} · veille anti-régression (I1/I2/I4/R4a/R4b)`
              : "Aucun scan encore"}
          </p>
        </div>
        <Button
          onClick={runScan}
          disabled={running}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="rocket-run-scan"
          aria-label="Lancer un scan Rocket"
        >
          {running
            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            : <RefreshCw className="w-4 h-4 mr-2" />}
          Lancer un scan
        </Button>
      </div>

      {!report ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
          <ShieldAlert className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">
            Aucun scan Rocket. Cliquer « Lancer un scan » pour démarrer.
          </p>
        </div>
      ) : (
        <>
          {/* Synthèse */}
          <div className={`${colorCfg.bg} border border-zinc-700 rounded-xl p-6`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                {summary.total === 0
                  ? <ShieldCheck className={`w-10 h-10 ${colorCfg.text}`} />
                  : <ShieldAlert className={`w-10 h-10 ${colorCfg.text}`} />}
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Violations détectées</p>
                  <p className={`text-5xl font-bold ${colorCfg.text}`}>{summary.total ?? 0}</p>
                  <p className={`text-sm mt-1 ${colorCfg.text}`}>{colorCfg.label}</p>
                </div>
              </div>
              <div className="text-sm text-zinc-300 space-y-1">
                <p>🔴 {bySev.critical || 0} critiques (exploitables)</p>
                <p>🟠 {bySev.high || 0} élevées</p>
                <p>🟡 {bySev.warning || 0} warnings</p>
                <p>🔵 {bySev.dette || 0} dette (à nettoyer)</p>
                {report.commit_sha && (
                  <p className="text-xs text-zinc-500">commit {report.commit_sha.slice(0, 8)}</p>
                )}
              </div>
            </div>
          </div>

          {/* Par règle */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <h2 className="text-white font-semibold mb-3">Par règle</h2>
            <div className="flex flex-wrap gap-2">
              {Object.keys(byRule).length === 0
                ? <p className="text-emerald-300 text-sm">Aucune violation — terrain propre ✓</p>
                : Object.entries(byRule).map(([rule, n]) => (
                  <div key={rule} className="bg-zinc-800/40 rounded-lg px-3 py-2 text-xs">
                    <span className="text-white font-semibold">{n}</span>
                    <span className="text-zinc-400 ml-2">{RULE_LABEL[rule] || rule}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Findings (cap 50) */}
          {findings.length > 0 && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
              <h2 className="px-4 py-3 border-b border-zinc-800 text-white font-semibold">
                Violations ({findings.length})
              </h2>
              <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                <table className="w-full min-w-[640px]">
                  <thead className="sticky top-0 bg-zinc-900">
                    <tr className="border-b border-zinc-800">
                      <th className="text-left px-4 py-2 text-sm text-zinc-400">Sévérité</th>
                      <th className="text-left px-4 py-2 text-sm text-zinc-400">Règle</th>
                      <th className="text-left px-4 py-2 text-sm text-zinc-400">Fichier:ligne</th>
                      <th className="text-left px-4 py-2 text-sm text-zinc-400">Extrait</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findings.slice(0, 50).map((f, i) => (
                      <tr key={i} className="border-b border-zinc-800/50">
                        <td className="px-4 py-2">
                          <Badge className={SEV[f.severity] || "bg-zinc-700 text-zinc-300"}>
                            {f.severity}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-xs text-zinc-300">{f.rule}</td>
                        <td className="px-4 py-2 text-xs text-zinc-400 whitespace-nowrap">
                          {f.file}:{f.line}
                        </td>
                        <td className="px-4 py-2 text-xs text-zinc-500 max-w-md truncate" title={f.snippet}>
                          <code>{f.snippet}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {findings.length > 50 && (
                <p className="px-4 py-2 text-xs text-zinc-500 italic">
                  … +{findings.length - 50} autres (voir scan complet via l'API)
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
